import React, { useState, useEffect } from 'react';
import { Stack, Typography, TextField, MenuItem } from '@mui/material';
import {
  Info as InfoIcon,
} from '@mui/icons-material';

interface BoldedTextProps {
  text: string;
  substrings: string[];
}

// Utility to bold all occurrences of a substring (or substrings)
const BoldedText: React.FC<BoldedTextProps> = ({ text, substrings }) => {
  if (!substrings || substrings.length === 0) return <>{text}</>;

  const innerRegex = substrings
    .map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
  const regex = new RegExp(`%%(${innerRegex})%%`, 'g');

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let idx = 0;

  while ((match = regex.exec(text)) !== null) {
    // Text before the match (preserves all spaces)
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    // Bold only the inner part (match[1])
    parts.push(<b key={idx++}>{match[1]}</b>);
    lastIndex = match.index + match[0].length;
  }
  // Remainder of the string after the last match
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  return <>{parts}</>;
};

interface InlineEditFieldProps {
  label: string;
  value: string;
  onSave: (newValue: string) => void;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  editable?: boolean;
  alwaysEditing?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  type?: string;
  inputProps?: object;
  select?: boolean;
  options?: { value: string; label: string }[];
  loading?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  onInfoClick?: () => void;
  info?: string;
  boldSubstrings?: string[];
}

const InlineEditField: React.FC<InlineEditFieldProps> = ({
  label,
  value,
  onSave,
  multiline = false,
  minRows,
  maxRows,
  editable = true,
  alwaysEditing = false,
  required = false,
  error = false,
  helperText = '',
  type,
  inputProps,
  select = false,
  options = [],
  loading = false,
  fullWidth = false,
  disabled = false,
  info,
  onInfoClick,
  boldSubstrings = [],
}) => {
  const [editValue, setEditValue] = useState<string>(value);
  const [isEditing, setIsEditing] = useState<boolean>(alwaysEditing);
  const shouldEdit = (isEditing || value.trim() === '') && editable && !disabled;

  useEffect(() => {
    setEditValue(value);
    if (alwaysEditing || value === '') setIsEditing(true);
    else setIsEditing(false);
  }, [value, alwaysEditing]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(!!alwaysEditing);
  };

  const handleClick = () => {
    if (editable && !disabled && value !== '') {
      setIsEditing(true);
    }
  };

  // Helper for rendering helperText with bolded substrings
  const renderHelperText = () =>
    helperText && (boldSubstrings?.length > 0)
      ? <BoldedText text={helperText} substrings={boldSubstrings} />
      : null;

  // Render input field based on props
  const renderInputField = () => {
    if (!shouldEdit) {
      return (
        <Typography
          variant="body2"
          sx={{
            width: "100%",
            cursor: editable && !disabled ? 'pointer' : 'default',
            wordBreak: 'break-word',
            minHeight: 40,
            display: 'flex',
            alignItems: 'center',
            color: disabled ? 'text.disabled' : undefined,
            opacity: disabled ? 0.6 : 1,
            whiteSpace: 'pre-wrap',
          }}
          onClick={handleClick}
        >
          <BoldedText text={value} substrings={boldSubstrings} />
        </Typography>
      );
    }

    if (select) {
      return (
        <TextField
          select
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          size="small"
          onBlur={handleSave}
          fullWidth
          required={required}
          error={error}
          helperText={renderHelperText()}
          autoFocus={!alwaysEditing}
          inputProps={inputProps}
          disabled={disabled}
        >
          {loading ? (
            <MenuItem value="">
              <em>Loading...</em>
            </MenuItem>
          ) : (
            options?.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))
          )}
        </TextField>
      );
    }

    return (
      <TextField
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        size="small"
        multiline={multiline}
        minRows={multiline ? minRows : undefined}
        maxRows={multiline ? maxRows : undefined}
        onBlur={handleSave}
        fullWidth
        autoFocus={!alwaysEditing}
        required={required}
        error={error}
        helperText={renderHelperText()}
        type={type}
        autoComplete="off"
        inputProps={inputProps}
        disabled={disabled}
      />
    );
  };

  // Use vertical layout only if condition is met
  if (multiline) {
    return (
      <Stack spacing={0.5} sx={{ mb: 1, py: 1, width: '100%' }}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ fontWeight: 600, mb: 0.5 }}
        >
          {label}: {" "}
          {info && (
            <InfoIcon
              color="action"
              sx={{ cursor: 'pointer', fontSize: 12 }}
              onClick={onInfoClick}
            />
          )}
        </Typography>
        {renderInputField()}
      </Stack>
    );
  }

  // Otherwise, use horizontal layout
  return (
    <Stack spacing={0.5} sx={{ mb: 1, py: 1, width: '100%' }}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ fontWeight: 600, mb: 0.5 }}
      >
        {label}:
      </Typography>
      {renderInputField()}
    </Stack>
  );
};

export default InlineEditField;